import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  QrCodeScanner as ScanIcon,
  FlipCameraAndroid as FlipIcon,
} from '@mui/icons-material';
import { Scanner } from '@yudiel/react-qr-scanner';

interface QRScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (result: string) => void;
  title?: string;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  open,
  onClose,
  onScan,
  title = 'QR-Code scannen',
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const handleScan = (result: any) => {
    console.log('QR Code detected:', result);

    // Extract the text from the result
    const qrText = result?.[0]?.rawValue || result?.text || result;

    if (qrText) {
      onScan(qrText);
      handleClose();
    }
  };

  const handleError = (error: unknown) => {
    console.error('QR Scanner error:', error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'QR-Scanner konnte nicht gestartet werden. Bitte überprüfen Sie die Kamera-Berechtigungen.';
    setError(errorMessage);
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const handleClose = () => {
    setError(null);
    setIsPaused(false);
    onClose();
  };

  const retryScanner = () => {
    setError(null);
    setIsPaused(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          m: 1,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScanIcon />
          {title}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Error Alert */}
        <Collapse in={!!error}>
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ m: 2, mb: 0 }}
            action={
              <Button color="inherit" size="small" onClick={retryScanner}>
                Erneut versuchen
              </Button>
            }
          >
            {error}
          </Alert>
        </Collapse>

        {/* Scanner View */}
        {!error && open && (
          <Box sx={{ position: 'relative', backgroundColor: '#000', minHeight: 300 }}>
            <Scanner
              onScan={handleScan}
              onError={handleError}
              paused={isPaused}
              constraints={{
                facingMode: facingMode,
                aspectRatio: 1,
              }}
              components={{
                finder: true,
              }}
              styles={{
                container: {
                  width: '100%',
                  height: 'auto',
                  minHeight: '300px',
                },
                video: {
                  width: '100%',
                  height: 'auto',
                },
              }}
              formats={['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39']}
            />

            {/* Scanning Instruction Overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 50,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  textAlign: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                }}
              >
                QR-Code in den Rahmen einpassen
              </Typography>
            </Box>

            {/* Camera Controls */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                display: 'flex',
                gap: 1,
              }}
            >
              {/* Switch Camera Button */}
              <IconButton
                onClick={switchCamera}
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                }}
              >
                <FlipIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Richten Sie die Kamera auf einen QR-Code
        </Typography>
        <Button onClick={handleClose}>
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
};